import Component from '@glimmer/component';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { variation } from 'ember-launch-darkly';
import { eq } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import can from 'ember-can/helpers/can';
import { HdsLinkStandalone } from '@hashicorp/design-system-components/components';
import { FlexGrid, Flex, ZeroState } from 'core/components';
import { boxMargin } from 'core/modifiers';
import GetStarted from 'hcp/components/page/hvns/cards/get-started';
import LearnMore from 'hcp/components/page/hvns/cards/learn-more';
import FlowDiagram from 'hcp/components/page/hvns/cards/flow-diagram';

/**
 *
 * `model` is an object that contains the project and networks
 * @argument model;
 * @type {Object}
 *
 */
/**
 *
 * `PageHvnsListZeroState` renders a group of helper content when a user does not have any HVNs.
 *  This includes a Get started stepper, learn more and a video.
 *
 *
 * ```
 * <Page::Hvns::List::ZeroState
 *   @model={{model}}
 * />
 * ```
 *
 * @class PageHvnsListZeroState
 *
 */
export default class PageHvnsListZeroState extends Component {
  @service analytics;

  // TODO: [TCE-2007] Remove HVN activation A/B test analytics instrumentation.
  captureAnalyticsEventABTestHvnCreateIntentZeroState = () => {
    const abTestVariation = variation('hcp-ui-ab-test-hvn-activation-create');

    this.analytics.trackEvent(
      'external_ui_hvn_activation_create_intent_button_clicked',
      { abTestVariation, buttonLocation: 'zero-state-content' }
    );
  };

  <template>
    {{#if (eq @model.networks.length 0)}}
      <div data-test-hvns-zero-state>
        {{#if
          (eq
            (variation "hcp-ui-ab-test-hvn-activation-create")
            "onboarding-stepper"
          )
        }}
          <FlexGrid class="hvn-page__width-constrained" as |G|>
            <G.Item @xs="12" @sm="12" @md="12" @lg="4">
              <Flex @direction="column" @gap="lg" {{boxMargin "0 0 lg 0"}}>
                <GetStarted
                  @networks={{@model.networks}}
                  @project={{@model.project}}
                />
                <LearnMore />
              </Flex>
            </G.Item>
            <G.Item @xs="12" @sm="12" @md="12" @lg="8">
              <Flex @direction="column" @gap="lg" {{boxMargin "0 0 lg 0"}}>
                <FlowDiagram />
              </Flex>
            </G.Item>
          </FlexGrid>
        {{else}}
          <ZeroState as |ZS|>
            <ZS.Header>
              {{t "components.page.hvns.list.empty.header"}}
            </ZS.Header>
            <ZS.Message>
              {{t "components.page.hvns.list.empty.message"}}
            </ZS.Message>
            {{#if (can "create network" @model.networks)}}
              <ZS.Action>
                {{! TODO: [TCE-2007] Remove HVN activation A/B test analytics instrumentation. }}
                <HdsLinkStandalone
                  @route="cloud.orgs.detail.projects.detail.hvns.create"
                  @text={{t "components.page.hvns.list.empty.action"}}
                  @icon="plus"
                  data-test-hvns-zero-state-create-link
                  {{on
                    "click"
                    this.captureAnalyticsEventABTestHvnCreateIntentZeroState
                  }}
                />
              </ZS.Action>
            {{/if}}
          </ZeroState>
        {{/if}}
      </div>
    {{/if}}
  </template>
}
